import { useMemo } from 'react';
import { Close } from '@mui/icons-material';
import { Chip, Drawer } from '@mui/material';
import { FirmDailyNavCompanyConfigRow } from 'api';
import {
  dailyNavDateLabel,
  dailyNavNextDayLabel,
  selectDailyNavApplyDates,
  selectDailyNavGoverningVersions,
} from 'common/constants/dailyNav';
import DailyNavCompanyVersionHistory from 'components/DailyNavCompanyVersionHistory';
import DailyNavFirmVersionHistory from 'components/DailyNavFirmVersionHistory';
import { Alert, Box, IconButton, List, ListItem, ListItemText, Stack, Text } from 'components/ui';
import {
  useGetCompanyDailyNavConfigHistory,
  useGetDailyNavConfigApplyPreview,
  useGetFirmDailyNavConfigHistory,
} from 'services/hooks/dailyNav';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';

export type DailyNavSetupPanelContext =
  | { kind: 'firm' }
  | { kind: 'companies'; companies: FirmDailyNavCompanyConfigRow[] }
  | { kind: 'company'; company: FirmDailyNavCompanyConfigRow };

type DailyNavSetupContextPanelProps = {
  open: boolean;
  onClose: () => void;
  firmId: number;
  context: DailyNavSetupPanelContext;
};

// A record whose open NAV day is still on an older company version than the saved one.
export const isCompanyNavDayBehind = (row: FirmDailyNavCompanyConfigRow) =>
  Boolean(row.applied_nav_date) &&
  row.company_config_id != null &&
  row.applied_company_config_id != null &&
  row.applied_company_config_id !== row.company_config_id;

const FirmContext = ({ firmId }: { firmId: number }) => {
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetFirmDailyNavConfigHistory(firmId);
  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const { anchoredVersionId, openVersionId, navDate, nextNavDate, hasDivergence } = useMemo(
    () => selectDailyNavGoverningVersions(applyPreview),
    [applyPreview]
  );

  return (
    <Stack spacing={2}>
      {hasDivergence && (
        <Alert severity="warning" data-testid={DAILY_NAV_SETUP_TEST_IDS.panelDivergenceAlert}>
          The NAV day in progress ({dailyNavDateLabel(navDate)}) is still running on an earlier version of these
          settings. {dailyNavNextDayLabel(nextNavDate)}. You can apply the latest settings to it at Review.
        </Alert>
      )}
      <DailyNavFirmVersionHistory
        history={history}
        hasMoreHistory={hasMoreHistory}
        isLoadingMoreHistory={isLoadingMoreHistory}
        loadMoreHistory={loadMoreHistory}
        anchoredVersionId={anchoredVersionId}
        openVersionId={openVersionId}
        hasDivergence={hasDivergence}
        governingNavDate={navDate}
        nextNavDate={nextNavDate}
      />
    </Stack>
  );
};

const CompaniesContext = ({ companies }: { companies: FirmDailyNavCompanyConfigRow[] }) => {
  const behind = companies.filter(isCompanyNavDayBehind);
  return (
    <Stack spacing={2}>
      <Text variant="h4">Open NAV day</Text>
      {behind.length ? (
        <>
          <Text variant="body2">
            These companies&apos; NAV day in progress still uses their previous settings. You can apply the latest
            settings at Review.
          </Text>
          <List dense data-testid={DAILY_NAV_SETUP_TEST_IDS.panelBehindList}>
            {behind.map(row => (
              <ListItem key={row.company_id} disablePadding>
                <ListItemText
                  primary={row.company_name}
                  secondary={`${dailyNavDateLabel(row.applied_nav_date)} NAV is on earlier settings`}
                />
              </ListItem>
            ))}
          </List>
        </>
      ) : (
        <Text variant="body2" color="text.secondary">
          Every company&apos;s NAV day in progress is on its latest saved settings.
        </Text>
      )}
    </Stack>
  );
};

const CompanyContext = ({ firmId, company }: { firmId: number; company: FirmDailyNavCompanyConfigRow }) => {
  const { history, loadMoreHistory, hasMoreHistory, isLoadingMoreHistory } = useGetCompanyDailyNavConfigHistory(
    firmId,
    company.company_id
  );
  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId, company.company_id);
  const { anchoredCompanyVersionId, navDate, nextNavDate } = useMemo(
    () => selectDailyNavGoverningVersions(applyPreview),
    [applyPreview]
  );
  // From the count of records that would move, as on the company tab: firm cascades
  // change version ids far more often than this company's rules change.
  const { applicableDates, recordCount } = useMemo(
    () => selectDailyNavApplyDates(applyPreview?.dates),
    [applyPreview?.dates]
  );
  const hasDivergence = applicableDates.length > 0 && recordCount > 0;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Text variant="h4">{company.company_name}</Text>
        {company.applied_nav_date && (
          <Chip
            size="small"
            color={isCompanyNavDayBehind(company) ? 'warning' : 'success'}
            label={`NAV day ${dailyNavDateLabel(company.applied_nav_date)}`}
          />
        )}
      </Stack>
      {hasDivergence && (
        <Alert severity="warning">
          {company.company_name}&apos;s NAV day in progress ({dailyNavDateLabel(navDate)}) uses its previous settings.
          You can apply the latest settings at Review.
        </Alert>
      )}
      <DailyNavCompanyVersionHistory
        history={history}
        hasMoreHistory={hasMoreHistory}
        isLoadingMoreHistory={isLoadingMoreHistory}
        loadMoreHistory={loadMoreHistory}
        anchoredCompanyVersionId={anchoredCompanyVersionId}
        hasDivergence={hasDivergence}
        governingNavDate={navDate}
        nextNavDate={nextNavDate}
      />
    </Stack>
  );
};

// History and live status for wherever the wizard currently is.
const DailyNavSetupContextPanel = ({ open, onClose, firmId, context }: DailyNavSetupContextPanelProps) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    data-testid={DAILY_NAV_SETUP_TEST_IDS.contextPanel}
    PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Text variant="h6">History &amp; status</Text>
        <IconButton
          aria-label="Close history and status"
          onClick={onClose}
          data-testid={DAILY_NAV_SETUP_TEST_IDS.contextPanelCloseButton}>
          <Close />
        </IconButton>
      </Stack>
      {context.kind === 'firm' && <FirmContext firmId={firmId} />}
      {context.kind === 'companies' && <CompaniesContext companies={context.companies} />}
      {context.kind === 'company' && <CompanyContext key={context.company.company_id} firmId={firmId} company={context.company} />}
    </Box>
  </Drawer>
);

export default DailyNavSetupContextPanel;
